import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '@/components/shared/TimeSeriesChart';
import { Card } from '@/components/ui/Card';
import { format, subHours } from 'date-fns';
import { apiService } from '@/utils/api';
import { useAnalytics } from './AnalyticsContext';
import { FiWifi } from 'react-icons/fi';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

const WiFiSignalPage: React.FC = () => {
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);
  
  // Default settings
  const [endTimeState, setEndTimeState] = useState<Date>(new Date());
  const [startTimeState, setStartTimeState] = useState<Date>(subHours(new Date(), 24));
  
  const [interval, setInterval] = useState<string>('15m');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSignalStrength, setCurrentSignalStrength] = useState<number>(0);
  const [averageSignalStrength, setAverageSignalStrength] = useState<number>(0);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  
  // Format as ISO strings with UTC timezone
  const formattedStartTime = startTimeState.toISOString();
  const formattedEndTime = endTimeState.toISOString();
  
  // Determine signal quality based on strength
  const getSignalQuality = (signalStrength: number): { label: string; colorClass: string } => {
    if (signalStrength >= -50) {
      return { label: 'Excellent', colorClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20' };
    } else if (signalStrength >= -60) {
      return { label: 'Good', colorClass: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20' };
    } else if (signalStrength >= -70) {
      return { label: 'Fair', colorClass: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20' };
    } else {
      return { label: 'Poor', colorClass: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20' };
    }
  };
  
  // Fetch signal data when device ID or time parameters change
  useEffect(() => {
    if (!deviceId) return;
    
    const fetchSignalData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          device_id: deviceId,
          metrics: ['wifi_strength'],
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          interval: interval
        };
        
        const data = await apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', params);
        
        // Reset states when data is empty or invalid
        if (!data || data.length === 0 || data[0].points.length === 0) {
          setCurrentSignalStrength(0);
          setAverageSignalStrength(0);
          setTimeSeriesData([]);
          return;
        }
        
        // Get latest signal strength as current value
        const latestPoint = data[0].points[data[0].points.length - 1];
        setCurrentSignalStrength(latestPoint.value);
        
        // Calculate average signal strength
        const sum = data[0].points.reduce((acc: number, point: TimeSeriesPoint) => acc + point.value, 0);
        const avg = sum / data[0].points.length;
        setAverageSignalStrength(parseFloat(avg.toFixed(1)));
        
        // Save time series data for chart
        setTimeSeriesData(data);
      } catch (err) {
        console.error('Error fetching signal data:', err);
        setError('Failed to fetch signal data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSignalData();
  }, [deviceId, formattedStartTime, formattedEndTime, interval]);
  
  const handleIntervalChange = (newInterval: string) => {
    setInterval(newInterval);
  };
  
  const handleDateRangeChange = (newStartTime: string, newEndTime: string) => {
    // The received times are already in UTC format, so we need to convert them to local time
    const startDate = new Date(newStartTime);
    const endDate = new Date(newEndTime);
    
    // No need to adjust for timezone since Date constructor already handles UTC 'Z' suffix
    setStartTimeState(startDate);
    setEndTimeState(endDate);
  };
  
  const currentQuality = getSignalQuality(currentSignalStrength);
  const averageQuality = getSignalQuality(averageSignalStrength);
  
  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>WiFi Signal</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>Monitor your device's WiFi signal strength</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-4">
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Current Signal</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{currentSignalStrength} dBm</span>
                  </h3>
                  <p>Signal Strength</p>
                  {/* <p>Last updated: <span className="font-semibold">{format(new Date(), 'HH:mm:ss')}</span></p> */}
                </div>
                {currentSignalStrength !== 0 && (
                  <div className="relative group">
                    <div className={`tag gap-1 font-bold border-0 ${currentQuality.colorClass}`}>
                      <span>{currentQuality.label}</span>
                    </div>
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>Signal strength interpretation:</p>
                      <p>-30 to -50 dBm: Excellent</p>
                      <p>-50 to -60 dBm: Good</p>
                      <p>-60 to -70 dBm: Fair</p>
                      <p>Below -70 dBm: Poor</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Average Signal</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{averageSignalStrength} dBm</span>
                  </h3>
                  <p>Signal Strength</p>
                  <p>Last 24 hours</p>
                </div>
                {averageSignalStrength !== 0 && (
                  <div className="relative group">
                    <div className={`tag gap-1 font-bold border-0 ${averageQuality.colorClass}`}>
                      <span>{averageQuality.label}</span>
                    </div>
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>Signal strength interpretation:</p>
                      <p>-30 to -50 dBm: Excellent</p>
                      <p>-50 to -60 dBm: Good</p>
                      <p>-60 to -70 dBm: Fair</p>
                      <p>Below -70 dBm: Poor</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {timeSeriesData.length > 0 ? (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>WiFi Signal Strength Over Time</h4>
                    <p>Monitor signal strength fluctuations</p>
                  </div>
                </div>
                
                {loading && (
                  <div className="flex justify-center items-center h-64">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={500}
                    data={timeSeriesData}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKey="wifi_strength"
                    yAxisTitle="Signal Strength (dBm)"
                    tooltipValueSuffix=" dBm"
                    seriesName="WiFi Signal Strength (dBm)"
                    chartColor="#4f46e5"
                    title="WiFi Signal Strength"
                    subtitle="Monitor signal strength over time"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <p>No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WiFiSignalPage; 